/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * SERVICE: ValidadorImovelService
 * VERSÃO:  1.0.0
 * ------------------------------------------------------------
 * Motor ÚNICO de validação do dossiê do imóvel.
 *
 * MODELO: 2 estados (CONFORME / NAO_CONFORME)
 * FONTE DE VERDADE: campos + tabela IBAPE + documentos anexados
 *
 * REGRAS:
 *   ✔ Não muta dados
 *   ✔ Determinístico
 *   ✔ Devolve lista nominal de pendências
 *   ✔ Uma pendência por campo
 * ============================================================
 */

import { TABELA_IBAPE } from '../components/imovel/TabelaIbape';

/* ============================================================
   STATUS
   ============================================================ */

export const STATUS = {
  CONFORME: 'CONFORME',
  NAO_CONFORME: 'NAO_CONFORME',
};

/* ============================================================
   CATÁLOGO DE FASES
   ------------------------------------------------------------
   Cada fase lista seus campos obrigatórios.
   tipo:
     'texto'      → verifica preenchimento (default)
     'numero'     → verifica número > 0
     'coordenada' → verifica lat/lng válidas
     'documento'  → verifica PDF anexado ou referência
     'array'      → verifica array não-vazio
     'ibape'      → verifica vida útil IBAPE compatível
   ============================================================ */

export const FASES = {
  REQUERENTE: {
    label: 'Requerente',
    ordem: 1,
    campos: [
      { path: 'requerente.nome',             label: 'Nome do requerente',       tipo: 'texto' },
      { path: 'requerente.processoJudicial', label: 'Processo judicial',        tipo: 'texto' },
    ],
  },
  INGESTAO: {
    label: 'Ingestão',
    ordem: 2,
    campos: [
      { path: 'logradouro', label: 'Logradouro',  tipo: 'texto' },
      { path: 'numero',     label: 'Número',      tipo: 'numero-ou-semnumero' },
      { path: 'bairro',     label: 'Bairro',      tipo: 'texto' },
      { path: 'cidade',     label: 'Cidade',      tipo: 'texto' },
      { path: 'uf',         label: 'UF',          tipo: 'texto' },
      { path: 'cep',        label: 'CEP',         tipo: 'texto' },
      { path: 'lat',        label: 'Latitude',    tipo: 'coordenada-lat' },
      { path: 'lng',        label: 'Longitude',   tipo: 'coordenada-lng' },
    ],
  },
  IDENTIFICACAO: {
    label: 'Identificação',
    ordem: 3,
    campos: [
      { path: 'areaTerreno',  label: 'Área do terreno',       tipo: 'numero' },
      { path: 'frente',       label: 'Frente principal',      tipo: 'numero' },
      { path: 'rgi',          label: 'RGI / Matrícula',       tipo: 'texto' },
      { path: 'cartografico', label: 'Código cartográfico',   tipo: 'texto' },
    ],
  },
  DOCUMENTOS: {
    label: 'Documentos',
    ordem: 4,
    campos: [
      { path: 'documentos.matricula', label: 'Matrícula atualizada (PDF)', tipo: 'documento' },
      { path: 'documentos.iptu',      label: 'IPTU / cadastro fiscal (PDF)', tipo: 'documento' },
    ],
  },
  CARACTERIZACAO: {
    label: 'Caracterização',
    ordem: 5,
    campos: [
      { path: 'uso',               label: 'Uso',                    tipo: 'texto' },
      { path: 'tipologia',         label: 'Tipologia',              tipo: 'texto' },
      { path: 'padrao',            label: 'Padrão construtivo',     tipo: 'texto' },
      { path: 'idadeReal',         label: 'Idade real',             tipo: 'numero' },
      { path: 'estadoConservacao', label: 'Estado de conservação',  tipo: 'texto' },
      { path: 'vidaUtilIBAPE',     label: 'Vida útil (IBAPE)',      tipo: 'ibape' },
    ],
  },
  PLANEJAMENTO: {
    label: 'Planejamento',
    ordem: 6,
    campos: [
      { path: 'logAgendamento', label: 'Tentativa de contato registrada', tipo: 'array' },
    ],
  },
};

export const ORDEM_FASES = Object.keys(FASES).sort(
  (a, b) => FASES[a].ordem - FASES[b].ordem
);

/* ============================================================
   HELPERS DE VALIDAÇÃO POR TIPO
   ============================================================ */

function getPath(obj, path) {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);
}

function preenchido(v) {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'number') return !Number.isNaN(v) && v > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return Boolean(v);
}

function numeroValido(v) {
  const n = parseFloat(String(v || '').replace(/[^\d,.-]/g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0;
}

function latValida(v) {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return false;
  if (n < -90 || n > 90) return false;
  if (n === 0) return false;
  return true;
}

function lngValida(v) {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return false;
  if (n < -180 || n > 180) return false;
  if (n === 0) return false;
  return true;
}

function documentoAnexado(doc) {
  if (!doc) return false;
  if (doc.status === 'pendente' || doc.status === 'enviado') return true;
  if (doc.file) return true;
  if (doc.meta && Object.keys(doc.meta).length > 0) return true;
  if (doc.url) return true;
  return false;
}

function ibapeCompativel(dossie) {
  const { uso, tipologia, padrao, vidaUtilIBAPE } = dossie;
  if (!uso || !tipologia || !padrao) return false;
  if (!vidaUtilIBAPE || Number(vidaUtilIBAPE) <= 0) return false;
  const match = TABELA_IBAPE.find(
    (item) => item.uso === uso && item.tipologia === tipologia && item.padrao === padrao
  );
  if (!match) return false;
  return Number(vidaUtilIBAPE) === Number(match.vida);
}

/* ============================================================
   VALIDAÇÃO POR CAMPO
   ============================================================ */

function validarCampo(dossie, campo) {
  const valor = getPath(dossie, campo.path);
  const tipo = campo.tipo || 'texto';

  switch (tipo) {
    case 'texto':
      return preenchido(valor) ? null : { campo: campo.path, label: campo.label, motivo: 'Vazio' };

    case 'numero':
      return numeroValido(valor) ? null : { campo: campo.path, label: campo.label, motivo: 'Deve ser um número positivo' };

    case 'numero-ou-semnumero': {
      // Válido se: (a) tem número OU (b) está marcado semNumero e tem lote+quadra+setor
      if (preenchido(valor)) return null;
      const semNumero = dossie.semNumero === true;
      if (semNumero) {
        const lote = preenchido(dossie.lote);
        const quadra = preenchido(dossie.quadra);
        const setor = preenchido(dossie.setor);
        if (lote && quadra && setor) return null;
        return {
          campo: campo.path,
          label: campo.label,
          motivo: 'Sem número exige Lote + Quadra + Setor',
        };
      }
      return { campo: campo.path, label: campo.label, motivo: 'Vazio' };
    }

    case 'coordenada-lat':
      return latValida(valor) ? null : { campo: campo.path, label: campo.label, motivo: 'Latitude inválida ou ausente' };

    case 'coordenada-lng':
      return lngValida(valor) ? null : { campo: campo.path, label: campo.label, motivo: 'Longitude inválida ou ausente' };

    case 'documento':
      return documentoAnexado(valor) ? null : { campo: campo.path, label: campo.label, motivo: 'Documento não anexado' };

    case 'array':
      return Array.isArray(valor) && valor.length > 0 ? null : { campo: campo.path, label: campo.label, motivo: 'Nenhum registro' };

    case 'ibape':
      return ibapeCompativel(dossie) ? null : {
        campo: campo.path,
        label: campo.label,
        motivo: 'Sem par uso/tipologia/padrão correspondente na tabela IBAPE',
      };

    default:
      return preenchido(valor) ? null : { campo: campo.path, label: campo.label, motivo: 'Vazio' };
  }
}

/* ============================================================
   API PÚBLICA
   ============================================================ */

/**
 * Valida uma fase.
 * @param {object} dossie
 * @param {string} faseId
 * @returns {{ fase, label, status, pendencias, total, preenchidos }}
 */
export function validarFase(dossie, faseId) {
  const fase = FASES[faseId];
  if (!fase) {
    return {
      fase: faseId,
      label: faseId,
      status: STATUS.NAO_CONFORME,
      pendencias: [{ campo: faseId, label: faseId, motivo: 'Fase desconhecida' }],
      total: 0,
      preenchidos: 0,
    };
  }

  const pendencias = [];
  let preenchidos = 0;

  for (const campo of fase.campos) {
    const erro = validarCampo(dossie, campo);
    if (erro) {
      pendencias.push(erro);
    } else {
      preenchidos++;
    }
  }

  const status = pendencias.length === 0 ? STATUS.CONFORME : STATUS.NAO_CONFORME;

  return {
    fase: faseId,
    label: fase.label,
    status,
    pendencias,
    total: fase.campos.length,
    preenchidos,
  };
}

/**
 * Valida o imóvel inteiro. Retorna status global + por fase.
 * @param {object} dossie — { requerente, logradouro, lat, ... }
 * @returns {{ status, conforme, total_pendencias, por_fase, pendencias }}
 */
export function validarImovel(dossie) {
  const porFase = {};
  const pendencias = [];
  let todasConformes = true;

  for (const faseId of ORDEM_FASES) {
    const res = validarFase(dossie, faseId);
    porFase[faseId] = res;

    if (res.status === STATUS.NAO_CONFORME) {
      todasConformes = false;
      res.pendencias.forEach((p) => {
        pendencias.push({ ...p, fase: faseId });
      });
    }
  }

  return {
    status: todasConformes ? STATUS.CONFORME : STATUS.NAO_CONFORME,
    conforme: todasConformes,
    total_pendencias: pendencias.length,
    por_fase: porFase,
    pendencias,
  };
}

/**
 * Retorna apenas a lista de pendencias (formato plano).
 */
export function listarPendencias(dossie) {
  return validarImovel(dossie).pendencias;
}

/**
 * Determina prioridade da tarefa a partir da pendência.
 * Regra: campos estruturais → ALTA; resto → MEDIA.
 */
export function prioridadeDaPendencia(pendencia) {
  const camposAlta = [
    'lat', 'lng',
    'areaTerreno', 'frente',
    'rgi', 'cartografico',
    'documentos.matricula',
    'vidaUtilIBAPE',
    'uso', 'tipologia', 'padrao',
  ];
  return camposAlta.includes(pendencia.campo) ? 'ALTA' : 'MEDIA';
}

/**
 * Determina categoria da tarefa a partir da pendência.
 * - Documento / coordenada / IBAPE → VERIFICACAO
 * - Campo crítico divergente       → DIVERGENCIA
 * - Contato com terceiros          → CONTATO
 */
export function categoriaDaPendencia(pendencia) {
  if (pendencia.campo.startsWith('documentos.')) return 'VERIFICACAO';
  if (pendencia.campo === 'vidaUtilIBAPE')       return 'DIVERGENCIA';
  if (pendencia.campo === 'rgi' || pendencia.campo === 'cartografico') return 'VERIFICACAO';
  if (pendencia.campo === 'lat' || pendencia.campo === 'lng')           return 'VERIFICACAO';
  return 'VERIFICACAO';
}

/**
 * Badge visual por status.
 */
export function badgeStatus(status) {
  if (status === STATUS.CONFORME) {
    return {
      label: 'Conforme',
      classe: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    };
  }
  return {
    label: 'Não conforme',
    classe: 'bg-rose-500/10 border-rose-500/30 text-rose-400',
  };
}

/* ============================================================
   EXPORT DEFAULT
   ============================================================ */

const ValidadorImovelService = {
  STATUS,
  FASES,
  ORDEM_FASES,
  validarFase,
  validarImovel,
  listarPendencias,
  prioridadeDaPendencia,
  categoriaDaPendencia,
  badgeStatus,
};

export default ValidadorImovelService;